import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError, fmtNum } from "@/lib/api";
import { downloadCsv } from "@/lib/downloadCsv";
import { useMasters, opt, partiesByRole } from "@/lib/useMasters";
import { PageHeader, Card, Field, TextInput, NumberInput, SelectInput, SectionTitle, StatusBadge, CrudActions } from "@/components/kit";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { InwardEditDialog, OutwardEditDialog } from "@/components/TxnEditDialog";
import { Download, Upload, Loader2, Plus, Trash2, FileSpreadsheet } from "lucide-react";

const DEFAULT_AS_ON = "2026-04-01";

const KINDS = [
  { value: "opening", label: "Opening stock", hint: "Stock as on FY start (01-04-2026)" },
  { value: "inward", label: "Bulk inward", hint: "Entries from 01-04-2026 onwards" },
  { value: "outward", label: "Bulk outward", hint: "Dispatches after opening / inward" },
  { value: "movements", label: "All movements", hint: "One file with type = OPENING / INWARD / OUTWARD" },
];

function emptyManualRow() {
  return {
    warehouse_id: "",
    stack_id: "",
    stock_owner_id: "",
    stock_item_id: "",
    bag_type_id: "",
    bags: "",
    net_weight: "",
    variety: "",
    remarks: "",
  };
}

export default function Onboard() {
  const m = useMasters();
  const qc = useQueryClient();
  const [kind, setKind] = useState("opening");
  const [asOn, setAsOn] = useState(DEFAULT_AS_ON);
  const [skipCapacity, setSkipCapacity] = useState(true);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [commitResult, setCommitResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [manualRows, setManualRows] = useState([emptyManualRow()]);

  const ownerOpts = opt(partiesByRole(m.parties, "Stock Owner"), (p) => `${p.name} (${p.code})`);
  const itemOpts = opt(m.items, (i) => `${i.code} — ${i.name}`);
  const bagOpts = opt(m.bagTypes, (b) => b.name);
  const whOpts = opt(m.warehouses, (w) => `${w.code} — ${w.name}`);

  const invalidateStock = () => {
    qc.invalidateQueries({ queryKey: ["masters"] });
    qc.invalidateQueries({ queryKey: ["reg-inward"] });
    qc.invalidateQueries({ queryKey: ["reg-outward"] });
    qc.invalidateQueries({ queryKey: ["reg-ledger"] });
    qc.invalidateQueries({ queryKey: ["import-posted"] });
    qc.invalidateQueries({ queryKey: ["txn"] });
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/txn/import/template", { params: { kind } });
      const lines = (data.csv || "").replace(/^\uFEFF/, "").split(/\r?\n/).filter((l) => l.length);
      const rows = lines.map((line) => {
        // simple CSV split for template (no embedded commas in samples)
        return line.split(",");
      });
      downloadCsv(data.filename || `srm-${kind}-template.csv`, rows);
      toast.success("Template downloaded");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const runPreview = async () => {
    if (!file) return toast.error("Choose a CSV or Excel file");
    setBusy(true);
    setCommitResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      fd.append("as_on_date", asOn);
      const { data } = await api.post("/txn/import/preview", fd);
      setPreview(data);
      toast.success(`Preview: ${data.ok} ok, ${data.errors} with errors`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const runCommit = async () => {
    if (!file) return toast.error("Choose a CSV or Excel file");
    if (!window.confirm(`Post ${kind} rows from this file? Stock will update. Do this only once.`)) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      fd.append("as_on_date", asOn);
      fd.append("skip_capacity", skipCapacity ? "true" : "false");
      const { data } = await api.post("/txn/import/commit", fd);
      setCommitResult(data);
      invalidateStock();
      toast.success(`Posted ${data.posted} · failed ${data.failed}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const postManualOpening = async () => {
    const rows = manualRows
      .filter((r) => r.warehouse_id && r.stack_id && r.stock_owner_id && r.stock_item_id && r.bags && r.net_weight)
      .map((r) => ({
        date: asOn,
        warehouse_id: Number(r.warehouse_id),
        stack_id: Number(r.stack_id),
        stock_owner_id: Number(r.stock_owner_id),
        stock_item_id: Number(r.stock_item_id),
        bag_type_id: r.bag_type_id ? Number(r.bag_type_id) : null,
        bags: Number(r.bags),
        net_weight: Number(r.net_weight),
        variety: r.variety || null,
        remarks: r.remarks || `Opening stock as on ${asOn}`,
      }));
    if (!rows.length) return toast.error("Add at least one complete opening row");
    if (!window.confirm(`Post ${rows.length} opening stock row(s) as on ${asOn}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/txn/import/commit-json", {
        kind: "opening",
        as_on_date: asOn,
        skip_capacity: skipCapacity,
        rows,
      });
      setCommitResult(data);
      invalidateStock();
      toast.success(`Posted ${data.posted} opening row(s)`);
      setManualRows([emptyManualRow()]);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.rows) setPreview({ total: d.rows.length, ok: 0, errors: d.rows.length, rows: d.rows });
      toast.error(typeof d === "string" ? d : d?.message || apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const kindMeta = useMemo(() => KINDS.find((k) => k.value === kind), [kind]);

  return (
    <div data-testid="onboard-page">
      <PageHeader
        title="Onboarding & Bulk Upload"
        subtitle="Opening stock as on 01-04-2026, then bulk inward / outward history — no labour or bag charges"
      />

      <Card className="mb-6 p-5 bg-emerald-50/60 border-emerald-100">
        <SectionTitle>Recommended sequence</SectionTitle>
        <ol className="list-decimal pl-5 text-sm text-stone-700 space-y-1">
          <li>Create masters (warehouses, stacks, stock owners, items, bag types).</li>
          <li>Post <strong>opening stock</strong> as on <strong>01-04-2026</strong> (per stack / lot).</li>
          <li>Upload <strong>inward</strong> then <strong>outward</strong> from 01-04-2026 to today (date order).</li>
          <li>Check Stack Layout / Lots / Registers, then use live Inward &amp; Outward for new work.</li>
          <li>To correct opening or bulk uploads, open the <strong>Posted entries</strong> tab (or Registers) and use Edit / Delete.</li>
        </ol>
      </Card>

      <Tabs defaultValue="upload">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="upload" data-testid="ontab-upload">CSV / Excel upload</TabsTrigger>
          <TabsTrigger value="manual" data-testid="ontab-manual">Opening stock (manual)</TabsTrigger>
          <TabsTrigger value="posted" data-testid="ontab-posted">Posted entries</TabsTrigger>
        </TabsList>

        <TabsContent value="upload">
          <div className="grid gap-6 lg:grid-cols-3">
            <Card className="p-5 lg:col-span-1 space-y-4">
              <SectionTitle>File</SectionTitle>
              <Field label="Import type" required>
                <SelectInput
                  options={KINDS.map((k) => ({ value: k.value, label: k.label }))}
                  value={kind}
                  onChange={(e) => { setKind(e.target.value); setPreview(null); setCommitResult(null); }}
                  placeholder=""
                  data-testid="import-kind"
                />
              </Field>
              <p className="text-xs text-stone-500">{kindMeta?.hint}</p>
              <Field label="Opening / as-on date">
                <TextInput type="date" value={asOn} onChange={(e) => setAsOn(e.target.value)} data-testid="import-as-on" />
              </Field>
              <label className="flex items-center gap-2 text-sm text-stone-700">
                <input type="checkbox" checked={skipCapacity} onChange={(e) => setSkipCapacity(e.target.checked)} />
                Skip stack capacity checks (recommended for onboarding)
              </label>
              <Field label="Upload file (.csv / .xlsx)">
                <input
                  type="file"
                  accept=".csv,.xlsx,.xlsm,.xls,text/csv"
                  onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setCommitResult(null); }}
                  data-testid="import-file"
                  className="block w-full text-sm text-stone-600 file:mr-3 file:rounded-full file:border-0 file:bg-emerald-800 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-white"
                />
              </Field>
              {file && (
                <div className="flex items-center gap-2 text-xs text-stone-500">
                  <FileSpreadsheet size={14} /> {file.name}
                </div>
              )}
              <div className="flex flex-wrap gap-2 pt-2">
                <button type="button" onClick={downloadTemplate}
                  className="inline-flex h-10 items-center gap-2 rounded-full border border-stone-200 bg-white px-4 text-sm font-semibold text-stone-700 hover:bg-stone-50">
                  <Download size={16} /> Template
                </button>
                <button type="button" onClick={runPreview} disabled={busy}
                  className="inline-flex h-10 items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 text-sm font-semibold text-emerald-900 hover:bg-emerald-100 disabled:opacity-50"
                  data-testid="import-preview-btn">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Preview
                </button>
                <button type="button" onClick={runCommit} disabled={busy}
                  className="inline-flex h-10 items-center gap-2 rounded-full bg-emerald-800 px-4 text-sm font-semibold text-white hover:bg-emerald-900 disabled:opacity-50"
                  data-testid="import-commit-btn">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : null} Post file
                </button>
              </div>
              <p className="text-xs text-stone-400">
                Codes must match Masters: warehouse code, stack code, party code, item code, bag type name.
              </p>
            </Card>

            <div className="lg:col-span-2 space-y-4">
              <PreviewCard preview={preview} />
              <ResultCard result={commitResult} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="manual">
          <Card className="p-5">
            <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
              <div>
                <SectionTitle>Opening stock by stack</SectionTitle>
                <p className="text-sm text-stone-500">One row = one lot slice (warehouse + stack + owner + item).</p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Field label="As on">
                  <TextInput type="date" value={asOn} onChange={(e) => setAsOn(e.target.value)} />
                </Field>
                <button type="button" onClick={() => setManualRows((r) => [...r, emptyManualRow()])}
                  className="inline-flex h-10 items-center gap-2 rounded-full border border-stone-200 px-3 text-sm font-semibold text-stone-700 hover:bg-stone-50">
                  <Plus size={16} /> Add row
                </button>
                <button type="button" onClick={postManualOpening} disabled={busy || m.loading}
                  className="inline-flex h-10 items-center gap-2 rounded-full bg-emerald-800 px-4 text-sm font-semibold text-white hover:bg-emerald-900 disabled:opacity-50"
                  data-testid="manual-opening-post">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : null} Post opening stock
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[980px]">
                <thead className="bg-stone-50 text-left text-[11px] uppercase tracking-wide text-stone-500">
                  <tr>
                    <th className="px-2 py-2">Warehouse</th>
                    <th className="px-2 py-2">Stack</th>
                    <th className="px-2 py-2">Stock owner</th>
                    <th className="px-2 py-2">Item</th>
                    <th className="px-2 py-2">Bag type</th>
                    <th className="px-2 py-2 text-right">Bags</th>
                    <th className="px-2 py-2 text-right">Net Qtl</th>
                    <th className="px-2 py-2">Variety</th>
                    <th className="px-2 py-2 w-10" />
                  </tr>
                </thead>
                <tbody>
                  {manualRows.map((r, idx) => {
                    const stacks = opt(
                      m.stacks.filter((s) => String(s.warehouse_id) === String(r.warehouse_id)),
                      (s) => s.code,
                    );
                    return (
                      <tr key={idx} className="border-t border-stone-100">
                        <td className="px-2 py-2">
                          <SelectInput options={whOpts} value={r.warehouse_id} placeholder="WH"
                            onChange={(e) => setManualRows((rows) => rows.map((x, i) => i === idx ? { ...x, warehouse_id: e.target.value, stack_id: "" } : x))} />
                        </td>
                        <td className="px-2 py-2">
                          <SelectInput options={stacks} value={r.stack_id} placeholder="Stack"
                            onChange={(e) => setManualRows((rows) => rows.map((x, i) => i === idx ? { ...x, stack_id: e.target.value } : x))} />
                        </td>
                        <td className="px-2 py-2">
                          <SelectInput options={ownerOpts} value={r.stock_owner_id} placeholder="Owner"
                            onChange={(e) => setManualRows((rows) => rows.map((x, i) => i === idx ? { ...x, stock_owner_id: e.target.value } : x))} />
                        </td>
                        <td className="px-2 py-2">
                          <SelectInput options={itemOpts} value={r.stock_item_id} placeholder="Item"
                            onChange={(e) => setManualRows((rows) => rows.map((x, i) => i === idx ? { ...x, stock_item_id: e.target.value } : x))} />
                        </td>
                        <td className="px-2 py-2">
                          <SelectInput options={bagOpts} value={r.bag_type_id} placeholder="Bag"
                            onChange={(e) => setManualRows((rows) => rows.map((x, i) => i === idx ? { ...x, bag_type_id: e.target.value } : x))} />
                        </td>
                        <td className="px-2 py-2">
                          <NumberInput value={r.bags} onChange={(e) => setManualRows((rows) => rows.map((x, i) => i === idx ? { ...x, bags: e.target.value } : x))} />
                        </td>
                        <td className="px-2 py-2">
                          <NumberInput value={r.net_weight} onChange={(e) => setManualRows((rows) => rows.map((x, i) => i === idx ? { ...x, net_weight: e.target.value } : x))} />
                        </td>
                        <td className="px-2 py-2">
                          <TextInput value={r.variety} onChange={(e) => setManualRows((rows) => rows.map((x, i) => i === idx ? { ...x, variety: e.target.value } : x))} />
                        </td>
                        <td className="px-2 py-2">
                          <button type="button" className="text-stone-400 hover:text-red-600" disabled={manualRows.length === 1}
                            onClick={() => setManualRows((rows) => rows.filter((_, i) => i !== idx))}>
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <ResultCard result={commitResult} />
          </Card>
        </TabsContent>

        <TabsContent value="posted">
          <PostedEntries onChanged={invalidateStock} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PostedEntries({ onChanged }) {
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["import-posted", filter],
    queryFn: async () => (await api.get("/txn/import/posted", { params: { kind: filter } })).data,
  });

  const remove = async (row) => {
    const num = row.doc_number || row.inward_number || row.outward_number;
    const path = row.kind === "outward" ? "outward" : "inward";
    if (!window.confirm(`Delete ${num}? Bags and weight will be rolled back through the lot ledger.`)) return;
    try {
      await api.delete(`/txn/${path}/${row.id}`);
      toast.success(`${num} deleted`);
      refetch();
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const onSaved = () => {
    setEditing(null);
    refetch();
    onChanged?.();
  };

  const rows = data?.rows || [];

  return (
    <Card className="overflow-hidden" data-testid="posted-entries">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stone-100 px-4 py-3">
        <div>
          <SectionTitle>Opening &amp; bulk-uploaded entries</SectionTitle>
          <p className="text-xs text-stone-500 mt-0.5">
            Edit bags / weight anytime. Changing warehouse, stack, owner or item needs this to still be the latest movement on that lot.
          </p>
        </div>
        <SelectInput
          className="w-44"
          options={[
            { value: "all", label: "All types" },
            { value: "opening", label: "Opening only" },
            { value: "inward", label: "Bulk inward" },
            { value: "outward", label: "Bulk outward" },
          ]}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder=""
          data-testid="posted-filter"
        />
      </div>
      {isLoading ? (
        <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-emerald-700" size={28} /></div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-left text-[11px] uppercase tracking-wide text-stone-500">
              <tr>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Document</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">WH / Stack</th>
                <th className="px-4 py-3">Owner</th>
                <th className="px-4 py-3">Item</th>
                <th className="px-4 py-3">Lot</th>
                <th className="px-4 py-3 text-right">Bags</th>
                <th className="px-4 py-3 text-right">Net Qtl</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-stone-400">No opening or import entries yet</td></tr>
              )}
              {rows.map((r) => (
                <tr key={`${r.kind}-${r.id}`} className="border-t border-stone-100 even:bg-stone-50/50">
                  <td className="px-4 py-3">
                    <StatusBadge tone={r.kind === "opening" ? "emerald" : r.kind === "outward" ? "info" : "neutral"}>
                      {(r.source_type || r.kind || "").toUpperCase()}
                    </StatusBadge>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs font-semibold text-emerald-700">{r.doc_number}</td>
                  <td className="px-4 py-3">{r.date}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.warehouse_code}/{r.stack_code}</td>
                  <td className="px-4 py-3">{r.stock_owner_name}</td>
                  <td className="px-4 py-3">{r.item_name}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.lot_number}</td>
                  <td className="px-4 py-3 text-right font-semibold">{fmtNum(r.bags)}</td>
                  <td className="px-4 py-3 text-right">{fmtNum(r.net_weight)}</td>
                  <td className="px-4 py-3">
                    <CrudActions
                      testid={`posted-${r.kind}-${r.id}`}
                      onEdit={() => setEditing(r)}
                      onDelete={() => remove(r)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && editing.kind !== "outward" && (
        <InwardEditDialog record={editing} onClose={() => setEditing(null)} onSaved={onSaved} />
      )}
      {editing && editing.kind === "outward" && (
        <OutwardEditDialog record={editing} onClose={() => setEditing(null)} onSaved={onSaved} />
      )}
    </Card>
  );
}

function PreviewCard({ preview }) {
  if (!preview) {
    return (
      <Card className="p-8 text-center text-sm text-stone-400">
        Preview a file to validate warehouse / stack / party / item codes before posting.
      </Card>
    );
  }
  return (
    <Card className="overflow-hidden" data-testid="import-preview">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-stone-100 px-4 py-3">
        <div className="font-semibold text-stone-800">Preview</div>
        <div className="flex gap-2 text-xs">
          <StatusBadge tone="emerald">{preview.ok} ok</StatusBadge>
          <StatusBadge tone={preview.errors ? "danger" : "neutral"}>{preview.errors} errors</StatusBadge>
          <StatusBadge tone="neutral">{preview.total} total</StatusBadge>
        </div>
      </div>
      <div className="overflow-x-auto max-h-[420px]">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-stone-50 text-left text-[11px] uppercase tracking-wide text-stone-500">
            <tr>
              <th className="px-3 py-2">Row</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2 text-right">Bags</th>
              <th className="px-3 py-2 text-right">Qtl</th>
              <th className="px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {(preview.rows || []).map((r, i) => (
              <tr key={i} className="border-t border-stone-100">
                <td className="px-3 py-2 font-mono text-xs">{r.row}</td>
                <td className="px-3 py-2 uppercase text-xs">{r.kind}</td>
                <td className="px-3 py-2">{r.preview?.date || "—"}</td>
                <td className="px-3 py-2 text-right">{r.preview?.bags ?? "—"}</td>
                <td className="px-3 py-2 text-right">{r.preview?.net_weight ?? "—"}</td>
                <td className="px-3 py-2">
                  {r.ok ? (
                    <StatusBadge tone="success">OK</StatusBadge>
                  ) : (
                    <span className="text-xs text-red-700">{(r.errors || []).join("; ")}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function ResultCard({ result }) {
  if (!result) return null;
  return (
    <Card className="overflow-hidden" data-testid="import-result">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-stone-100 px-4 py-3">
        <div className="font-semibold text-stone-800">Post result</div>
        <div className="flex gap-2 text-xs">
          <StatusBadge tone="emerald">{result.posted} posted</StatusBadge>
          <StatusBadge tone={result.failed ? "danger" : "neutral"}>{result.failed} failed</StatusBadge>
        </div>
      </div>
      <div className="overflow-x-auto max-h-[320px]">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-left text-[11px] uppercase tracking-wide text-stone-500">
            <tr>
              <th className="px-3 py-2">Row</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Document</th>
              <th className="px-3 py-2">Lot</th>
              <th className="px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {(result.results || []).map((r, i) => (
              <tr key={i} className="border-t border-stone-100">
                <td className="px-3 py-2 font-mono text-xs">{r.row}</td>
                <td className="px-3 py-2 uppercase text-xs">{r.kind}</td>
                <td className="px-3 py-2 font-mono text-xs text-emerald-800">
                  {r.result?.inward_number || r.result?.outward_number || "—"}
                </td>
                <td className="px-3 py-2 font-mono text-xs">{r.result?.lot_number || "—"}</td>
                <td className="px-3 py-2">
                  {r.ok ? <StatusBadge tone="success">Posted</StatusBadge> : <span className="text-xs text-red-700">{r.error}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
